import type { ProductBadge, ProductEyebrow } from '#root/api/clients/product/data-contracts'

/**
 * Formats a product badge or eyebrow label, appending a formatted target publish date
 * if the badge or eyebrow is marked as "COMING SOON".
 *
 * @param badgeOrEyebrow - The badge or eyebrow object to format.
 * @param targetPublishDate - The target publish date to append if applicable.
 * @param featureConfigDateFormat - The date format to use for the appended date suffix (defaults to 'YYYY/MM').
 * @returns The formatted badge or eyebrow object with an updated label.
 * @category Utils
 *
 * The function:
 * - Checks if the badge/eyebrow id is "COMING SOON" (case-insensitive).
 * - If so, appends a formatted date suffix to the label using the feature flag date format.
 * - Returns the badge/eyebrow object with the updated label.
 */

export const formatBadgeOrEyebrow = (
  badgeOrEyebrow?: ProductBadge & ProductEyebrow,
  targetPublishDate?: string,
  featureConfigDateFormat: string = 'YYYY/MM'
) => {
  if (!badgeOrEyebrow) return
  if (isArray(badgeOrEyebrow) && !badgeOrEyebrow.length) return

  badgeOrEyebrow = isArray(badgeOrEyebrow) ? badgeOrEyebrow[0] : badgeOrEyebrow

  const suffix = targetPublishDate && badgeOrEyebrow?.id?.toUpperCase().replace(/\s+/g, '') === 'COMINGSOON'
    ? formatTargetPublishDate(targetPublishDate, featureConfigDateFormat)
    : ''

  return {
    ...badgeOrEyebrow,
    label: `${badgeOrEyebrow?.label || ''}${badgeOrEyebrow?.label && suffix ? ' - ' : ''}${suffix}`,
  }
}
